/**
 * Единый резолв sessionId (coreId) для upload/download вложений.
 *
 * Источники (порядок приоритета):
 * 1. `withAttachmentUploadSessionOverride` / WS `coreSessionId` (`getJsRequestSessionId`) — запрос getData/getFullData с middleware.
 * 2. `platformSessionId` элемента в registry (`registerElement`, обновляется при появлении sessionId в store).
 * 3. `appStore.sessionIdByFormId(formId)` — после doAction / setPageData.
 * 4. `pagesData` текущей формы (`sessionId`, `$sessionId`, `cloneOf` для клона).
 * 5. `appStore.currentSessionId`, перебор `pagesData` / dialogs.
 *
 * После F5: `pagesData` пустой до завершения `doAction`/`getPage` — upload до появления sessionId невозможен.
 */
import { useAppStore } from '@/stores/app'
import {
  getAttachmentUploadSessionOverride,
  getJsRequestSessionId,
  normalizePlatformSessionId,
} from '@/common/utils/jsRequestSessionContext'
import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'

/** formId в registry / formPath может приходить как number. */
export type AttachmentSessionFormId = string | number | null | undefined

export type AttachmentSessionResolveInput = {
  fieldName?: string | null
  formId?: AttachmentSessionFormId
  pageRegistryFormId?: AttachmentSessionFormId
  pageDataSlot?: AttachmentSessionFormId
  elementFormId?: AttachmentSessionFormId
  platformSessionIdFromRegistry?: unknown
  pageData?: unknown
  pagesDataSnapshot?: Record<string, unknown>
}

export type AttachmentSessionResolveResult = {
  sessionId: string | null
  source: string | null
}

function pickSessionId(source: string, value: unknown): AttachmentSessionResolveResult | null {
  const sessionId = normalizePlatformSessionId(value)
  if (!sessionId) return null
  return { sessionId, source }
}

/**
 * Собирает sessionId для upload вложений.
 */
export function resolveAttachmentUploadSessionId(
  input: AttachmentSessionResolveInput,
): AttachmentSessionResolveResult {
  const fromOverride = pickSessionId('uploadOverride', getAttachmentUploadSessionOverride())
  if (fromOverride) return fromOverride

  const fromJsRequest = pickSessionId('jsRequest', getJsRequestSessionId())
  if (fromJsRequest) return fromJsRequest

  const fromRegistry = pickSessionId(
    'registry.platformSessionId',
    input.platformSessionIdFromRegistry,
  )
  if (fromRegistry) return fromRegistry

  const formIds = [
    input.formId,
    input.elementFormId,
    input.pageRegistryFormId,
    input.pageDataSlot,
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean)

  const appStore = useAppStore()
  for (const fid of [...new Set(formIds)]) {
    const fromFormId = pickSessionId(
      `sessionIdByFormId:${fid}`,
      appStore.sessionIdByFormId?.(fid),
    )
    if (fromFormId) return fromFormId
  }

  const fromPageData = pickSessionId('pageData', readPlatformSessionIdFromPageEntry(input.pageData))
  if (fromPageData) return fromPageData

  const fromCurrent = pickSessionId('currentSessionId', appStore.currentSessionId)
  if (fromCurrent) return fromCurrent

  const pages = input.pagesDataSnapshot ?? (appStore.pagesData as Record<string, unknown>)
  for (const [pageKey, entry] of Object.entries(pages)) {
    const fromPage = pickSessionId(
      `pagesData:${pageKey}`,
      readPlatformSessionIdFromPageEntry(entry),
    )
    if (fromPage) return fromPage
  }

  return { sessionId: null, source: null }
}
